import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Clock, X, MessageSquare, Loader2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { api, apiErrorMessage } from "@/lib/api";
import { GRAVEDAD } from "./AuditoriaResumen";

const STATUS = {
  aprobada: { label: "Aprobada", Icon: Check, active: "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-600", badge: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" },
  mas_adelante: { label: "Más adelante", Icon: Clock, active: "bg-amber-500 text-white border-amber-500 hover:bg-amber-500", badge: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200" },
  descartada: { label: "Descartada", Icon: X, active: "bg-slate-600 text-white border-slate-600 hover:bg-slate-600", badge: "bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-200" },
};

function fmt(iso) {
  return iso ? new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }) : "";
}

function PlanItem({ item, riesgo, decision }) {
  const qc = useQueryClient();
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState(decision?.note || "");
  const g = riesgo ? GRAVEDAD[riesgo.gravedad] : null;

  const save = useMutation({
    mutationFn: async ({ status, note: n }) => (await api.put(`/plataforma-auditoria/decisions/${item.id}`, { status, note: n })).data,
    onSuccess: (d) => {
      toast.success(`${item.titulo}: ${STATUS[d.status].label}`);
      setNoteOpen(false);
      qc.invalidateQueries({ queryKey: ["plataforma-auditoria"] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const clear = useMutation({
    mutationFn: async () => (await api.delete(`/plataforma-auditoria/decisions/${item.id}`)).data,
    onSuccess: () => { toast.success("Decisión retirada"); setNote(""); qc.invalidateQueries({ queryKey: ["plataforma-auditoria"] }); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const current = decision?.status;
  const busy = save.isPending || clear.isPending;

  return (
    <div className="rounded-lg border border-border/70 p-4" data-testid={`plan-item-${item.id}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="font-semibold">{item.titulo}</h4>
            {g && <Badge className={`${g.cls} text-[10px]`}>{riesgo.gravedad}</Badge>}
            <Badge variant="outline" className="text-[10px]">{item.esfuerzo}</Badge>
            {current && <Badge className={`text-[10px] ${STATUS[current].badge}`} data-testid={`plan-status-${item.id}`}>{STATUS[current].label}</Badge>}
          </div>
          <p className="mt-1 text-sm text-foreground/85">{item.detalle}</p>
          {decision && (
            <p className="mt-1.5 text-xs text-muted-foreground" data-testid={`plan-decided-${item.id}`}>
              Decidido por <span className="font-medium text-foreground">{decision.decided_by_name}</span> el {fmt(decision.decided_at)}
              {decision.note && <> · <span className="italic">“{decision.note}”</span></>}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {Object.entries(STATUS).map(([key, s]) => (
            <Button key={key} size="sm" variant="outline" disabled={busy}
              className={`h-8 ${current === key ? s.active : ""}`}
              onClick={() => save.mutate({ status: key, note })}
              data-testid={`plan-decide-${key}-${item.id}`}>
              {busy && save.variables?.status === key ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <s.Icon className="mr-1 h-3.5 w-3.5" />}{s.label}
            </Button>
          ))}
          <Button size="sm" variant="ghost" className="h-8" onClick={() => setNoteOpen((o) => !o)} data-testid={`plan-note-toggle-${item.id}`}>
            <MessageSquare className="mr-1 h-3.5 w-3.5" />Nota
          </Button>
          {current && (
            <Button size="sm" variant="ghost" className="h-8 text-muted-foreground" onClick={() => clear.mutate()} disabled={busy} title="Quitar decisión" data-testid={`plan-clear-${item.id}`}>
              <Undo2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
      {noteOpen && (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Nota para esta medida (opcional): condiciones, quién se encarga, fecha…"
            className="text-sm" data-testid={`plan-note-input-${item.id}`} />
          <Button size="sm" disabled={busy || !current} onClick={() => save.mutate({ status: current, note })} data-testid={`plan-note-save-${item.id}`} title={!current ? "Elige primero una decisión" : ""}>
            Guardar nota
          </Button>
        </div>
      )}
    </div>
  );
}

export default function AuditoriaPlan({ plan, riesgos, decisions }) {
  const riesgoById = Object.fromEntries((riesgos || []).map((r) => [r.id, r]));
  const total = plan.reduce((n, b) => n + b.items.length, 0);
  const decided = plan.reduce((n, b) => n + b.items.filter((i) => decisions[i.id]).length, 0);
  return (
    <section data-testid="auditoria-plan">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="font-heading text-lg font-bold">Plan de acción · marca tu decisión</h2>
          <p className="text-sm text-muted-foreground">Cada medida puede marcarse como <b>Aprobada</b>, <b>Más adelante</b> o <b>Descartada</b>, con una nota. Queda registrado quién decidió y cuándo.</p>
        </div>
        <Badge variant="secondary" data-testid="plan-progress">{decided} de {total} decididas</Badge>
      </div>
      <div className="space-y-6">
        {plan.map((b) => (
          <Card key={b.bloque} className="border-border/70" data-testid={`plan-bloque-${b.bloque}`}>
            <CardContent className="p-4 sm:p-5">
              <h3 className="mb-3 font-heading text-base font-semibold">{b.titulo}</h3>
              <div className="space-y-3">
                {b.items.map((it) => <PlanItem key={it.id} item={it} riesgo={riesgoById[it.riesgo]} decision={decisions[it.id]} />)}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}
